import { es } from './catalog.es';
import { en } from './catalog.en';
import {
  Catalog,
  DEFAULT_LANGUAGE,
  LOCALE_TAG,
  Language,
} from './types';

const CATALOGS: Record<Language, Catalog> = { es, en };

/**
 * The language the app is currently painted in, held at module scope on
 * purpose: plain (non-React) helpers such as `getLabel` need to read it, and
 * they are always called during a render that `I18nProvider` has already
 * re-run after a language change.
 *
 * `I18nProvider` owns this value — nothing else should write to it.
 */
let activeLanguage: Language = DEFAULT_LANGUAGE;

export const getActiveLanguage = (): Language => activeLanguage;

/** @internal Called by `I18nProvider` only. */
export const setActiveLanguage = (language: Language): void => {
  activeLanguage = language;
};

/** BCP-47 tag for `toLocaleDateString` & friends in the active language. */
export const getLocaleTag = (language: Language = activeLanguage): string =>
  LOCALE_TAG[language] ?? LOCALE_TAG[DEFAULT_LANGUAGE];

export type TranslateVars = Record<string, string | number>;

const interpolate = (text: string, vars?: TranslateVars): string => {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
};

/**
 * Look up `key` in the active catalog, falling back to Spanish and finally to
 * `fallback` (or the key itself, which makes a missing string obvious in the
 * UI instead of rendering an empty gap).
 *
 * `{placeholders}` in the text are filled from `vars`.
 */
export const translate = (
  key: string,
  vars?: TranslateVars,
  fallback?: string,
): string => {
  const text =
    CATALOGS[activeLanguage]?.[key] ??
    CATALOGS[DEFAULT_LANGUAGE][key] ??
    fallback ??
    key;
  return interpolate(text, vars);
};

/**
 * Count-aware lookup over a `<base>.one` / `<base>.other` key pair. `count` is
 * always available to the text as `{count}`.
 */
export const translatePlural = (
  baseKey: string,
  count: number,
  vars?: TranslateVars,
): string =>
  translate(`${baseKey}.${count === 1 ? 'one' : 'other'}`, { count, ...vars });

/** Every key present in either catalog — used by the parity test. */
export const catalogKeys = (language: Language): string[] =>
  Object.keys(CATALOGS[language]);

/**
 * Display name for a row that carries its own English name in the database
 * (asset categories, asset locations). Falls back to the Spanish `name`
 * whenever there is no translation — a row an administrator added without
 * filling the field, or one whose Spanish name is already right ("Accounts",
 * proper nouns).
 *
 * Resolved here rather than server-side on purpose: the Apollo cache keys on
 * the row id, so a server-resolved name would go stale the moment somebody
 * switches language mid-session.
 */
export const localizedName = (
  row: { name?: string | null; nameEn?: string | null } | null | undefined,
): string => {
  if (!row) return '';
  if (activeLanguage === 'en' && row.nameEn) return row.nameEn;
  return row.name ?? '';
};
